import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  ArrowDownLeft,
  CalendarDays,
  ChevronRight,
  Eye,
  HandCoins,
  Search,
  X,
} from "lucide-react";
import Pagenation from "../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import { AppDispatch, RootState } from "../../../../store";
import { getReceivedHistory } from "../../../../Redux/Fund-Transfer-api/slice";
import { TransferHistoryQuery } from "../../../../Redux/Fund-Transfer-api/types";
import "../WalletHistory.css";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const statusClass = (status?: string) =>
  `wallet-hist-status wallet-hist-status-${(status || "pending")
    .toLowerCase()
    .replace(/[^a-z]+/g, "")}`;

const RecievedFunds = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { receivedHistory, loading, pagination } = useSelector(
    (state: RootState) => state.transfer,
  );

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  useEffect(() => {
    if (dateRange.from && !dateRange.to) return;

    const query: TransferHistoryQuery = {
      page,
      limit,
      sortOrder: "DESC",
      startDate: dateRange.from || undefined,
      endDate: dateRange.to || undefined,
    };
    dispatch(getReceivedHistory(query));
  }, [dispatch, page, limit, dateRange.from, dateRange.to]);

  const records = (receivedHistory || []).filter((t: any) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      t.transferNumber?.toLowerCase().includes(q) ||
      t.senderUserId?.toLowerCase().includes(q) ||
      t.senderName?.toLowerCase().includes(q)
    );
  });

  const totalRecords = pagination?.total ?? records.length;

  return (
    <section className="wallet-hist-page">
      <div className="wallet-hist-shell">
        <header className="wallet-hist-header">
          <div className="wallet-hist-header-left">
            <div className="wallet-hist-title-icon">
              <ArrowDownLeft size={22} strokeWidth={2} />
            </div>
            <div>
              <span className="wallet-hist-eyebrow">MY WALLET</span>
              <h1>Received Funds</h1>
              <p>Funds transferred to your wallet by other Salar users.</p>
            </div>
          </div>
          <span className="wallet-hist-count-pill">
            {totalRecords} records
          </span>
        </header>

        <div className="wallet-hist-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Entries per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option value={val} key={val}>
                {val} Entries
              </option>
            ))}
          </select>

          <div className="wallet-hist-date-picker">
            <DateRangePicker
              value={dateRange}
              onChange={(v) => {
                setDateRange(v);
                setPage(1);
              }}
            />
          </div>

          <div className="wallet-hist-search">
            <Search size={16} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search transfers..."
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center p-4">Loading...</div>
        ) : (
          <>
            <div className="wallet-hist-card-list is-desktop-list">
              {records.map((transfer: any) => (
                <article className="wallet-hist-card" key={transfer.id}>
                  <div className="wallet-hist-card-meta">
                    <div>
                      <span>Date</span>
                      <strong>{formatDate(transfer.createdAt)}</strong>
                    </div>
                    <div>
                      <span>F. Transaction No</span>
                      <strong>{transfer.transferNumber}</strong>
                    </div>
                    <div>
                      <span>Component</span>
                      <strong>{transfer.component}</strong>
                    </div>
                    <div className="wallet-hist-card-status">
                      <span>Status</span>
                      <span className={statusClass(transfer.status)}>
                        {transfer.status}
                      </span>
                    </div>
                  </div>
                  <div className="wallet-hist-card-body">
                    <div>
                      <p className="wallet-hist-desc">
                        Sender Partner ID:{" "}
                        <strong>{transfer.senderUserId}</strong>
                        <br />
                        Sender Partner Name:{" "}
                        <strong>{transfer.senderName || "N/A"}</strong>
                      </p>
                    </div>
                    <div className="wallet-hist-amount is-credit">
                      <small>Amount</small>
                      <strong>{formatCurrency(transfer.amount)}</strong>
                    </div>
                  </div>
                  <div className="wallet-hist-card-meta">
                    <div>
                      <span>Details</span>
                      <Link
                        className="wallet-hist-view-button"
                        to={`/RecievedFudsDetails/${transfer.transferNumber}`}
                      >
                        <Eye size={14} />
                        View Details
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="wallet-hist-mobile-list">
              {records.map((transfer: any) => (
                <Link
                  className="wallet-hist-mobile-card"
                  key={transfer.id}
                  to={`/RecievedFudsDetails/${transfer.transferNumber}`}
                >
                  <span className="wallet-hist-mobile-icon is-credit">
                    <HandCoins size={19} />
                  </span>
                  <span className="wallet-hist-mobile-copy">
                    <span className="wallet-hist-mobile-topline">
                      <span className={statusClass(transfer.status)}>
                        {transfer.status}
                      </span>
                      <span className="wallet-hist-mobile-date">
                        <CalendarDays size={13} />
                        {formatDate(transfer.createdAt)}
                      </span>
                    </span>
                    <strong className="wallet-hist-mobile-title">
                      {transfer.senderName || transfer.senderUserId}
                    </strong>
                    <span className="wallet-hist-mobile-desc">
                      {transfer.transferNumber}
                    </span>
                    <span className="wallet-hist-mobile-bottomline">
                      <strong>{formatCurrency(transfer.amount)}</strong>
                      <span>{transfer.component}</span>
                    </span>
                  </span>
                  <ChevronRight
                    size={17}
                    className="wallet-hist-mobile-chevron"
                  />
                </Link>
              ))}
            </div>

            {records.length === 0 && (
              <div className="wallet-hist-empty">
                <HandCoins size={28} />
                <strong>No received funds found</strong>
                <span>Change the search or selected date range.</span>
              </div>
            )}
          </>
        )}

        <div className="wallet-hist-pagination">
          {pagination && (
            <Pagenation
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default RecievedFunds;
